import { t } from "@/lib/i18n/interpolate";
import {
  asClause,
  ensureTerminal,
  fillFrame,
  properWords,
  stripEnd,
  tidy,
} from "@/lib/tool-kit/essay/sentence";
import { splitItems } from "@/lib/tool-kit/essay-templates/text";

/**
 * A news story in inverted-pyramid order: the most important facts first, then the
 * details that explain them, then the background, with nothing that cannot be cut from
 * the bottom.
 *
 * The conventions here are the ones journalism textbooks agree on: the lede answers who,
 * what, when and where (and, when it fits, why) in one sentence, usually under about 35
 * words; a nut graf says why the story matters; quotations are attributed with "said";
 * paragraphs are short; and a news story stops without a conclusion. The AP Stylebook
 * adds more detailed rules — dates, numbers, titles, state abbreviations — that this
 * module does not try to apply. The few it mentions (sentence-case headlines with no
 * final period, a weekday instead of "yesterday") are in the checks, and the page says
 * to confirm them against the current Stylebook.
 */

export interface NewsQuote {
  id: number;
  /** The speaker's exact words. */
  text: string;
  /** Name and title as they should read: "Principal Ana Ruiz". */
  source: string;
}

export interface NewsInput {
  headline: string;
  byline: string;
  /** Dateline city, typed as a name: "Maple Ridge". Shown in capitals. */
  city: string;
  /** The state or region, already abbreviated if the style calls for it. */
  state: string;
  who: string;
  /** What happened, as a past-tense verb phrase: "raised $4,200 for …". */
  what: string;
  /** Day or date: "Friday". */
  when: string;
  /** Place, with its preposition: "at the school's spring car wash". */
  where: string;
  /** Reason, introduced by its own word: "to help families through the winter". */
  why: string;
  /** The lede the student settled on; empty uses the first draft. */
  lede: string;
  nutGraf: string;
  /** Key details, one paragraph per line. */
  details: string;
  quotes: readonly NewsQuote[];
  /** Background, one paragraph per line. */
  background: string;
  /** What happens next. */
  next: string;
}

export const MAX_QUOTES = 3;

export function blankQuote(id: number): NewsQuote {
  return { id, text: "", source: "" };
}

export type NewsPartKind =
  | "lede"
  | "nut"
  | "detail"
  | "quote"
  | "background"
  | "next";

export interface NewsPart {
  kind: NewsPartKind;
  text: string;
  /** The writer has not supplied this yet; the text says what belongs here. */
  prompt: boolean;
}

export interface NewsLabels {
  /** The verb that attributes a quotation. */
  said: string;
  missing: Readonly<Record<string, string>>;
  prompts: Readonly<Record<NewsPartKind, string>>;
  ledeFrames?: readonly string[];
  /** The joiner between a dateline and the lede. */
  datelineDash: string;
}

/** Lede drafts. Move the time element if it reads better after the verb. */
export const LEDE_FRAMES = [
  "{who} {what} {when}.",
  "{who} {what} {when} {where}.",
  "{who} {what} {when} {why}.",
] as const;

/* ------------------------------------------------------------------ the lede */

/** The lede drafts the five Ws allow. Needs a who and a what. */
export function buildLedeDrafts(
  input: NewsInput,
  frames: readonly string[] = LEDE_FRAMES
): string[] {
  if (tidy(input.who) === "" || tidy(input.what) === "") {
    return [];
  }
  const proper = properWords([input.who, input.where]);
  const optional = (text: string) =>
    tidy(text) === "" ? null : stripEnd(text);
  const values: Record<string, string | null> = {
    who: stripEnd(input.who),
    what: asClause(input.what, proper),
    when: optional(input.when),
    where: optional(input.where),
    why: optional(input.why),
  };
  const drafts = frames
    .map((frame) => fillFrame(frame, values))
    .filter((text): text is string => text !== null);
  return [...new Set(drafts)];
}

/** "MAPLE RIDGE, Mich. —": the city in capitals, then the state, then a dash. */
export function datelinePrefix(
  input: NewsInput,
  labels: Pick<NewsLabels, "datelineDash">
): string {
  const city = tidy(input.city).toUpperCase();
  if (city === "") {
    return "";
  }
  const state = tidy(input.state);
  const place = state === "" ? city : `${city}, ${state}`;
  return `${place} ${labels.datelineDash} `;
}

/* ------------------------------------------------------------------ quotations */

const RE_OUTER_QUOTES = /^["“‘']+|["”’']+$/g;
const RE_FIRST_SENTENCE = /^([\s\S]+?[.!?])["”]?\s+(?=["“]?[A-Z])([\s\S]+)$/;
const RE_ENDS_QUESTION = /[?!]$/;
const RE_TRAILING = /[\s,;:]+$/;
const RE_CLOSING_PUNCT = /[.,;:]+$/;

/**
 * A quotation with its attribution in news style: after the first sentence, and with
 * "said". The speaker's closing period becomes a comma when the attribution follows;
 * a question mark or exclamation point stays.
 *
 *   “We were hoping for $2,000. This blew us away,” → “We were hoping for $2,000,”
 *   Priya Desai said. “This blew us away.”
 */
export function attributedQuote(
  text: string,
  source: string,
  said: string
): string {
  const body = tidy(text).replace(RE_OUTER_QUOTES, "").replace(RE_TRAILING, "");
  if (body === "") {
    return "";
  }
  const speaker = `${tidy(source)} ${said}`.trim();
  const split = RE_FIRST_SENTENCE.exec(body);
  const first = split ? split[1] : body;
  const rest = split ? split[2] : "";
  const closed = RE_ENDS_QUESTION.test(first)
    ? first
    : `${first.replace(RE_CLOSING_PUNCT, "")},`;
  const lead = `“${closed}” ${speaker}.`;
  return rest === "" ? lead : `${lead} “${ensureTerminal(rest)}”`;
}

/* ------------------------------------------------------------------ assembling */

function pushLines(
  parts: NewsPart[],
  kind: NewsPartKind,
  text: string,
  prompt: string,
  always: boolean
): void {
  const lines = splitItems(text);
  if (lines.length === 0) {
    if (always) {
      parts.push({ kind, text: prompt, prompt: true });
    }
    return;
  }
  for (const line of lines) {
    parts.push({ kind, text: line, prompt: false });
  }
}

/** The story's paragraphs in inverted-pyramid order. */
export function buildNewsParts(
  input: NewsInput,
  labels: NewsLabels
): NewsPart[] {
  const parts: NewsPart[] = [];
  const drafts = buildLedeDrafts(input, labels.ledeFrames);
  const lede = tidy(input.lede) === "" ? (drafts[0] ?? "") : tidy(input.lede);
  parts.push(
    lede === ""
      ? { kind: "lede", text: labels.prompts.lede, prompt: true }
      : {
          kind: "lede",
          text: `${datelinePrefix(input, labels)}${lede}`,
          prompt: false,
        }
  );
  pushLines(parts, "nut", input.nutGraf, labels.prompts.nut, true);
  const details = splitItems(input.details);
  const quotes = input.quotes
    .map((quote) =>
      attributedQuote(
        quote.text,
        quote.source || labels.missing.source,
        labels.said
      )
    )
    .filter((text) => text !== "");
  // Alternate: a detail, a quotation, a detail… so a quotation backs up the fact before it.
  const longest = Math.max(details.length, quotes.length);
  for (let index = 0; index < longest; index++) {
    const detail = details[index];
    if (detail !== undefined) {
      parts.push({ kind: "detail", text: detail, prompt: false });
    }
    const quote = quotes[index];
    if (quote !== undefined) {
      parts.push({ kind: "quote", text: quote, prompt: false });
    }
  }
  if (details.length === 0) {
    parts.push({ kind: "detail", text: labels.prompts.detail, prompt: true });
  }
  if (quotes.length === 0) {
    parts.push({ kind: "quote", text: labels.prompts.quote, prompt: true });
  }
  pushLines(
    parts,
    "background",
    input.background,
    labels.prompts.background,
    true
  );
  pushLines(parts, "next", input.next, labels.prompts.next, false);
  return parts;
}

/** The whole story as plain text: headline, byline, then the paragraphs. */
export function newsText(
  input: NewsInput,
  parts: readonly NewsPart[],
  labels: Pick<NewsLabels, "missing">
): string {
  const headline = tidy(input.headline) || labels.missing.headline;
  const byline = tidy(input.byline);
  const head = byline === "" ? [headline] : [headline, byline];
  return [...head, ...parts.map((part) => part.text)].join("\n\n");
}

/* ------------------------------------------------------------------ checks */

export type NewsCheckId =
  | "ledeLength"
  | "ledeSentence"
  | "relativeTime"
  | "paragraphs"
  | "headline"
  | "nutGraf";

export const NEWS_CHECK_IDS: readonly NewsCheckId[] = [
  "ledeLength",
  "ledeSentence",
  "relativeTime",
  "paragraphs",
  "headline",
  "nutGraf",
];

export interface NewsCheck {
  id: NewsCheckId;
  ok: boolean;
  /** The number the check measured, for the message: words in the lede, and so on. */
  value: number;
}

/** Newsroom guides recommend ledes of about 25–35 words; longer is a flag, not an error. */
export const LEDE_MAX_WORDS = 35;
/** A news paragraph is one or two sentences; this many words is a long one. */
export const PARAGRAPH_MAX_WORDS = 45;
export const HEADLINE_MAX_WORDS = 12;

const RE_WORDS = /[\p{L}\p{N}][\p{L}\p{N}'’.$,%-]*/gu;
const RE_SENTENCE_END = /[.!?]["”]?\s+(?=["“]?[A-Z])/g;
const RE_RELATIVE_TIME =
  /\b(?:yesterday|today|tonight|tomorrow|last night|this morning|this afternoon|this evening)\b/i;
const RE_ENDS_PERIOD = /\.$/;

function wordsIn(text: string): number {
  return text.match(RE_WORDS)?.length ?? 0;
}

function sentencesIn(text: string): number {
  return text === "" ? 0 : (text.match(RE_SENTENCE_END)?.length ?? 0) + 1;
}

/** The lede without its dateline. */
function ledeBody(text: string, dash: string): string {
  const at = text.indexOf(` ${dash} `);
  return at === -1 ? text : text.slice(at + dash.length + 2);
}

/**
 * Checks on the finished story. They look at length, sentence count, relative-time
 * words, paragraph length, and the headline's form, and cannot judge accuracy or
 * newsworthiness. A flag is a prompt to look again.
 */
export function checkNews(
  input: NewsInput,
  parts: readonly NewsPart[],
  labels: Pick<NewsLabels, "datelineDash">
): NewsCheck[] {
  const ledePart = parts.find((part) => part.kind === "lede" && !part.prompt);
  const lede = ledePart ? ledeBody(ledePart.text, labels.datelineDash) : "";
  const ledeWords = wordsIn(lede);
  const headline = tidy(input.headline);
  const written = parts.filter((part) => !part.prompt);
  const longest = Math.max(0, ...written.map((part) => wordsIn(part.text)));
  const hasNut = parts.some((part) => part.kind === "nut" && !part.prompt);
  // A quoted "today" is the speaker's word, not the reporter's.
  const story = written
    .filter((part) => part.kind !== "quote")
    .map((part) => part.text)
    .join(" ");
  return NEWS_CHECK_IDS.map((id): NewsCheck => {
    switch (id) {
      case "ledeLength":
        return {
          id,
          ok: lede !== "" && ledeWords <= LEDE_MAX_WORDS,
          value: ledeWords,
        };
      case "ledeSentence":
        return {
          id,
          ok: lede !== "" && sentencesIn(lede) === 1,
          value: sentencesIn(lede),
        };
      case "relativeTime":
        return { id, ok: !RE_RELATIVE_TIME.test(story), value: 0 };
      case "paragraphs":
        return { id, ok: longest <= PARAGRAPH_MAX_WORDS, value: longest };
      case "headline":
        return {
          id,
          ok:
            headline !== "" &&
            !RE_ENDS_PERIOD.test(headline) &&
            wordsIn(headline) <= HEADLINE_MAX_WORDS,
          value: wordsIn(headline),
        };
      default:
        return { id, ok: hasNut, value: 0 };
    }
  });
}

/** The check messages take {count}; helper keeps the template fill in one place. */
export function checkMessage(
  template: string,
  check: NewsCheck,
  format: (value: number) => string
): string {
  return t(template, { count: format(check.value) });
}
